import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { z } from "zod";

/**
 * Enum options extracted from Ruby IssuerConcern::Enums
 */
export const amfMonetaryTypeOptions = [
  { value: "standard", label: "Standard" },
  { value: "short_term", label: "Short Term" },
];

export const issuerTypeOptions = [
  { value: "corporate", label: "Corporate" },
  { value: "sovereign", label: "Sovereign" },
  { value: "municipal", label: "Municipal" },
  { value: "etf", label: "ETF" },
  { value: "commodity", label: "Commodity" },
  { value: "index", label: "Index" },
  { value: "credit_basket", label: "Credit Basket" },
  { value: "fund", label: "Fund" },
  { value: "cash", label: "Cash" },
  { value: "currency_pair", label: "Currency Pair" },
  { value: "spv", label: "SPV" },
  { value: "supranational", label: "Supranational" },
  { value: "na", label: "N/A" },
  { value: "non_financial_entity", label: "Non Financial Entity" },
  { value: "sensitivity", label: "Sensitivity" },
];

export const expositionIssuerTypeOptions = [
  { value: "corporate", label: "Corporate" },
  { value: "sovereign", label: "Sovereign" },
  { value: "various", label: "Various" },
  { value: "na", label: "N/A" },
];

export const expositionIssuerRatingOptions = [
  { value: "hy", label: "HY" },
  { value: "ig", label: "IG" },
  { value: "various", label: "Various" },
  { value: "na", label: "N/A" },
];

export const oldSectorOptions = [
  { value: "na", label: "N/A" },
  { value: "energy", label: "Energy" },
  { value: "materials", label: "Materials" },
  { value: "industrials", label: "Industrials" },
  { value: "consumer_discretionary", label: "Consumer Discretionary" },
  { value: "consumer_staples", label: "Consumer Staples" },
  { value: "health_care", label: "Health Care" },
  { value: "financials", label: "Financials" },
  { value: "information_technology", label: "Information Technology" },
  { value: "telecommunication_services", label: "Telecommunication Services" },
  { value: "utilities", label: "Utilities" },
  { value: "real_estate", label: "Real Estate" },
  { value: "various", label: "Various" },
  { value: "cash", label: "Cash" },
  { value: "sovereign", label: "Sovereign" },
  { value: "municipal", label: "Municipal" },
  { value: "supranational", label: "Supranational" },
  { value: "currency_pair", label: "Currency Pair" },
];

export const sectorOptions = [
  { value: "na", label: "N/A" },
  { value: "energy", label: "Energy" },
  { value: "materials", label: "Materials" },
  { value: "industrials", label: "Industrials" },
  { value: "consumer_discretionary", label: "Consumer Discretionary" },
  { value: "consumer_staples", label: "Consumer Staples" },
  { value: "health_care", label: "Health Care" },
  { value: "financials", label: "Financials" },
  { value: "information_technology", label: "Information Technology" },
  { value: "communication_services", label: "Communication Services" },
  { value: "utilities", label: "Utilities" },
  { value: "real_estate", label: "Real Estate" },
  { value: "various", label: "Various" },
  { value: "sovereign", label: "Sovereign" },
  { value: "municipal", label: "Municipal" },
  { value: "supranational", label: "Supranational" },
  { value: "currency_pair", label: "Currency Pair" },
  { value: "cash", label: "Cash" },
];

export const typeOfIdentificationCodeForIssuerGroupOptions = [
  { value: "1", label: "1" },
  { value: "9", label: "9" },
];

/**
 * Zod schema for Issuers filter form
 */
export const filterIssuersSchema = z.object({
  lei_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  direct_parent_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  ultimate_parent_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  source_contains: z.string().nullable().optional(),
  amf_monetary_type_contains: z.string().nullable().optional(),
  issuer_type_contains: z.string().nullable().optional(),
  exposition_issuer_type_contains: z.string().nullable().optional(),
  exposition_issuer_rating_contains: z.string().nullable().optional(),
  old_sector_contains: z.string().nullable().optional(),
  sector_contains: z.string().nullable().optional(),
  type_of_identification_code_for_issuer_group_contains: z
    .string()
    .nullable()
    .optional(),
});

export type FilterIssuersSchema = z.infer<typeof filterIssuersSchema>;

export const fields: Field[] = [
  {
    name: "lei_contains",
    label: "LEI",
    type: "input",
  },
  {
    name: "name_contains",
    label: "Name",
    type: "input",
  },
  {
    name: "direct_parent_name_contains",
    label: "Direct Parent",
    type: "input",
  },
  {
    name: "ultimate_parent_name_contains",
    label: "Ultimate Parent",
    type: "input",
  },
  {
    name: "source_contains",
    label: "Source",
    type: "input",
  },
  {
    name: "amf_monetary_type_contains",
    label: "AMF Monetary Type",
    type: "select",
    useTranslation: false,
    options: amfMonetaryTypeOptions,
  },
  {
    name: "issuer_type_contains",
    label: "Issuer Type",
    type: "select",
    useTranslation: false,
    options: issuerTypeOptions,
  },
  {
    name: "exposition_issuer_type_contains",
    label: "Exposition Issuer Type",
    type: "select",
    useTranslation: false,
    options: expositionIssuerTypeOptions,
  },
  {
    name: "exposition_issuer_rating_contains",
    label: "Exposition Issuer Rating",
    type: "select",
    useTranslation: false,
    options: expositionIssuerRatingOptions,
  },
  {
    name: "old_sector_contains",
    label: "Old Sector",
    type: "select",
    useTranslation: false,
    options: oldSectorOptions,
  },
  {
    name: "sector_contains",
    label: "Sector",
    type: "select",
    useTranslation: false,
    options: sectorOptions,
  },
  {
    name: "type_of_identification_code_for_issuer_group_contains",
    label: "Type of Identification Code",
    type: "select",
    useTranslation: false,
    options: typeOfIdentificationCodeForIssuerGroupOptions,
  },
];

export const client = "qtl - referential";

export function FilterIssuers() {
  return (
    <FilterTable
      client={client}
      schema={filterIssuersSchema}
      tableName="issuers"
      customBuildPath={() => `${client}/issuers`}
      fields={fields}
      columns={2}
    />
  );
}
